import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lifecyclePhaseLabel } from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../lib/api.js';
import { Callout } from './Callout.js';

/** What moving there actually does, in the producer's terms rather than the
 *  system's. Since 2026-10-29 no phase hides a section (`TABS_BY_LIFECYCLE_PHASE`),
 *  so the sentence says that rather than promising sections opening or closing;
 *  Archived is the one move with a consequence of its own. */
const PHASE_CONSEQUENCE: Record<string, string> = {
  ARCHIVED: 'Every section stays open, but team conversations become read-only. Reversible.',
};

const PHASE_CONSEQUENCE_DEFAULT = 'Every section of the workspace stays open in every phase.';

const OPERATIONAL_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  ANALYSIS: 'Analysis',
  PLANNING: 'Planning',
  SCOUTING: 'Scouting',
  SHORTLISTING: 'Shortlisting',
  BOOKING: 'Booking',
  PREPARING: 'Preparing',
  SHOOTING: 'Shooting',
  WRAP: 'Wrap',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
};

const HEALTH_LABEL: Record<string, string> = {
  ON_TRACK: 'On track',
  AT_RISK: 'At risk',
  BLOCKED: 'Blocked',
};

/**
 * The three state dimensions a producer controls, in one place (ADR 0008).
 *
 * They are deliberately not one control. **Lifecycle phase** is what the
 * producer declares about the production and it drives the navigation.
 * **Operational phase** and **health** are system-derived, and what a producer
 * sets there is an *override* that sits beside the derived value rather than
 * replacing it — so the system can keep computing, and anyone can later see
 * both what it thought and what a human said instead.
 *
 * Only the moves the API will accept are offered. A button that produces a 400
 * is worse than no button, so the allowed set comes from the server's own
 * transition matrix rather than a copy in here.
 */
export function ProjectStatePanel({
  projectId,
  derivedHealth,
  healthOverride,
  operationalPhase,
  suggestedOperationalPhase,
}: {
  projectId: string;
  derivedHealth: string;
  healthOverride: string | null;
  operationalPhase?: string | null;
  suggestedOperationalPhase?: string | null;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [opReason, setOpReason] = useState('');
  const [healthReason, setHealthReason] = useState('');

  const transitions = useQuery({
    queryKey: ['transitions', projectId],
    queryFn: () => api.projectTransitions(projectId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['projects'] });
    await queryClient.invalidateQueries({ queryKey: ['transitions', projectId] });
    await queryClient.invalidateQueries({ queryKey: ['activity', projectId] });
  };

  const movePhase = useMutation({
    mutationFn: (to: string) => api.setLifecyclePhase(projectId, to),
    onSuccess: async () => {
      setConfirming(null);
      await refresh();
    },
  });

  const setOperational = useMutation({
    mutationFn: (to: string | null) =>
      api.setOperationalPhase(projectId, to, to === null ? null : opReason),
    onSuccess: async () => {
      setOpReason('');
      await refresh();
    },
  });

  const setHealth = useMutation({
    mutationFn: (to: string | null) =>
      api.setHealthOverride(projectId, to, to === null ? null : healthReason),
    onSuccess: async () => {
      setHealthReason('');
      await refresh();
    },
  });

  const failure = movePhase.error ?? setOperational.error ?? setHealth.error;
  const error = failure instanceof ApiError ? failure : null;

  const current = transitions.data?.lifecyclePhase;

  return (
    <section data-testid="project-state-panel" style={{ display: 'grid', gap: 22 }}>
      {error ? (
        <Callout tone="danger" title="That change was refused" testId="project-state-error">
          {error.message}
        </Callout>
      ) : null}

      <div>
        <p className="kick">Production phase</p>
        <p
          className="mm"
          style={{ fontSize: 12.5, margin: '6px 0 12px', lineHeight: 1.6, maxWidth: '68ch' }}
        >
          Where the production is. You set this — the system never moves it, because the
          tabs you see follow it and navigation changing under you would be startling.
          Currently <strong>{current ? lifecyclePhaseLabel(current) : '—'}</strong>.
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {transitions.data?.allowedLifecyclePhases.map((phase) => (
            <button
              key={phase}
              type="button"
              className="ctl"
              data-testid={`move-to-${phase}`}
              disabled={movePhase.isPending}
              onClick={() => {
                setConfirming(phase);
              }}
            >
              Move to {lifecyclePhaseLabel(phase)}
            </button>
          ))}
        </div>

        {transitions.data?.allowedLifecyclePhases.length === 0 ? (
          <p className="mm" style={{ fontSize: 12.5, marginTop: 10 }}>
            No moves are available from here.
          </p>
        ) : null}

        {/* Confirmed, not immediate. The phase is a statement about the production
            made for everyone on it, so the consequence is stated before the change
            rather than discovered after it. */}
        {confirming ? (
          <div
            className="card1"
            style={{ padding: 16, marginTop: 14, maxWidth: '68ch' }}
            data-testid="phase-confirm"
          >
            <p style={{ fontSize: 13.5, fontWeight: 600 }}>
              Move this production to {lifecyclePhaseLabel(confirming)}?
            </p>
            <p className="mm" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.6 }}>
              {PHASE_CONSEQUENCE[confirming] ?? PHASE_CONSEQUENCE_DEFAULT}{' '}
              It applies to everyone on the project, and it is recorded in Activity.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="phase-confirm-apply"
                disabled={movePhase.isPending}
                onClick={() => {
                  movePhase.mutate(confirming);
                }}
              >
                {movePhase.isPending ? 'Moving…' : 'Move the production'}
              </button>
              <button
                type="button"
                className="ctl"
                data-testid="phase-confirm-cancel"
                onClick={() => {
                  setConfirming(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div>
        <p className="kick">Stage of work</p>
        <p
          className="mm"
          style={{ fontSize: 12.5, margin: '6px 0 12px', lineHeight: 1.6, maxWidth: '68ch' }}
        >
          Where the work actually sits. The system works this out from the project itself
          {suggestedOperationalPhase
            ? ` — it currently reads ${OPERATIONAL_LABEL[suggestedOperationalPhase] ?? suggestedOperationalPhase}`
            : ''}
          . You can say otherwise, and your answer sits beside the computed one rather than
          replacing it.
          {operationalPhase ? (
            <>
              {' '}
              <strong data-testid="operational-override">
                Set by hand to {OPERATIONAL_LABEL[operationalPhase] ?? operationalPhase}.
              </strong>
            </>
          ) : null}
        </p>

        <div style={{ display: 'grid', gap: 8, maxWidth: '48ch' }}>
          <input
            type="text"
            value={opReason}
            placeholder="Why — e.g. locked locations early, scouting is done"
            aria-label="Why the stage of work is set by hand"
            data-testid="operational-reason"
            onChange={(e) => {
              setOpReason(e.target.value);
            }}
            style={{ border: '1px solid var(--color-divider)', padding: '9px 12px', fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              aria-label="Set the stage of work"
              data-testid="operational-select"
              defaultValue=""
              disabled={setOperational.isPending || opReason.trim().length === 0}
              onChange={(e) => {
                if (e.target.value) setOperational.mutate(e.target.value);
              }}
              style={{ border: '1px solid var(--color-divider)', padding: '9px 12px', fontSize: 13 }}
            >
              <option value="">Set the stage…</option>
              {transitions.data?.operationalPhases.map((phase) => (
                <option key={phase} value={phase}>
                  {OPERATIONAL_LABEL[phase] ?? phase}
                </option>
              ))}
            </select>
            {operationalPhase ? (
              <button
                type="button"
                className="ctl"
                data-testid="operational-clear"
                onClick={() => {
                  setOperational.mutate(null);
                }}
              >
                Use the computed stage
              </button>
            ) : null}
          </div>
          {/* A reason is required by the API, so the control says so rather than
              letting the producer discover it in an error. */}
          {opReason.trim().length === 0 ? (
            <p className="mm" style={{ fontSize: 11.5 }}>
              A reason is required — an unexplained override is indistinguishable from a
              mistake once its author is on set.
            </p>
          ) : null}
        </div>
      </div>

      <div>
        <p className="kick">Health</p>
        <p
          className="mm"
          style={{ fontSize: 12.5, margin: '6px 0 12px', lineHeight: 1.6, maxWidth: '68ch' }}
        >
          Computed as <strong>{HEALTH_LABEL[derivedHealth] ?? derivedHealth}</strong>.
          {healthOverride ? (
            <>
              {' '}
              <strong data-testid="health-override">
                You have set it to {HEALTH_LABEL[healthOverride] ?? healthOverride}.
              </strong>
            </>
          ) : null}
        </p>

        <div style={{ display: 'grid', gap: 8, maxWidth: '48ch' }}>
          <input
            type="text"
            value={healthReason}
            placeholder="Why — e.g. the lead location fell through"
            aria-label="Why the health is overridden"
            data-testid="health-reason"
            onChange={(e) => {
              setHealthReason(e.target.value);
            }}
            style={{ border: '1px solid var(--color-divider)', padding: '9px 12px', fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {transitions.data?.healthValues.map((value) => (
              <button
                key={value}
                type="button"
                className="ctl"
                data-on={healthOverride === value ? '1' : '0'}
                data-testid={`health-${value}`}
                disabled={setHealth.isPending || healthReason.trim().length === 0}
                onClick={() => {
                  setHealth.mutate(value);
                }}
              >
                {HEALTH_LABEL[value] ?? value}
              </button>
            ))}
            {healthOverride ? (
              <button
                type="button"
                className="ctl"
                data-testid="health-clear"
                onClick={() => {
                  setHealth.mutate(null);
                }}
              >
                Use the computed health
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
